import { useMutation } from '@apollo/client'
import { Button, Grid } from '@mui/material'
import ToggleEditTextFieldMultiline from 'candidate/components/ToggleEditFormFields/ToggleEditTextFieldMultiline'
import {
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useEffect,
    useState,
} from 'react'
import useToast from 'shared/components/Toast/useToast'
import { UpdateCandidateNote } from 'shared/graphql/candidatePreferences.gql'
import { EditButtonContainer } from 'shared/style/styled'
import PreferenceSectionHeader from './PreferenceSectionHeader'
import { editModel } from './preference.types'

interface Props {
    notesData: string
    handleEditArray: (sectionName: string, cancelEdit?: boolean) => any
    editArray: editModel[]
    sectionName: string
    candidateId: string
    templateId: string | undefined
    refetch: () => void
    setUnsavedChangesExist: Dispatch<SetStateAction<boolean>>
}
const Notes: FC<Props> = ({
    notesData,
    handleEditArray,
    editArray,
    sectionName,
    candidateId,
    refetch,
    setUnsavedChangesExist,
    templateId,
}) => {
    const [preferencesNote, setPreferencesNote] = useState<string>(notesData)
    const [isEdit, setIsEdit] = useState<boolean>(false)
    const toast = useToast()
    const [mutate] = useMutation(UpdateCandidateNote)

    useEffect(() => {
        setPreferencesNote(notesData)
    }, [notesData])

    useEffect(() => {
        let isSectionEditable: boolean =
            editArray.find((ele) => ele.sectionName === sectionName)?.isEdit ||
            false
        setIsEdit(isSectionEditable)
    }, [editArray, sectionName])

    const resetForm = useCallback(() => {
        setPreferencesNote(notesData)
    }, [setPreferencesNote, notesData])

    const unsavedChangesExist = notesData !== preferencesNote

    useEffect(() => {
        setUnsavedChangesExist(unsavedChangesExist)
    }, [unsavedChangesExist, setUnsavedChangesExist])

    // reset form when we cancel out of editing
    useEffect(() => {
        if (!isEdit) resetForm()
    }, [isEdit, resetForm])

    const handleUpdate = async () => {
        try {
            const { data, errors } = await mutate({
                variables: {
                    candidateId,
                    note: preferencesNote,
                    templateId: templateId!,
                },
            })
            if (data?.updateCandidateNote.success) {
                await handleEditArray(sectionName, true)
                toast({
                    title: 'Success',
                    message: 'Your note has been saved',
                    severity: 'success',
                })
                refetch()
                setUnsavedChangesExist(false)
            } else {
                console.log(errors)
                toast({
                    severity: 'error',
                    title: 'Error',
                    message:
                        data?.updateCandidateNote.message ??
                        'Failed to update notes',
                })
            }
        } catch (error) {
            console.log(error)
            toast({
                severity: 'error',
                title: 'Error',
                message: 'Failed to update notes',
            })
        }
    }

    return (
        <>
            <PreferenceSectionHeader
                handleEditArray={handleEditArray}
                sectionName={sectionName}
                title="Recruitment Notes"
                subTitle="Do you have any additional recruiting updates for the
                        CPI team? Specifically, timing updates, advanced
                        rounds/offer stages that we should be aware of."
            />
            <Grid container spacing={2}>
                <Grid item xs={10}>
                    <ToggleEditTextFieldMultiline
                        edit={isEdit}
                        label="Notes"
                        multiline={true}
                        onChange={(e) => {
                            setPreferencesNote(e.target.value)
                            if (e.target.value !== notesData) {
                                setUnsavedChangesExist(true)
                            } else if (isEdit) {
                                setUnsavedChangesExist(false)
                            }
                        }}
                        value={preferencesNote}
                    />
                </Grid>

                {isEdit && (
                    <EditButtonContainer item xs={12}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                handleEditArray(sectionName, true)
                                resetForm()
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleUpdate}>Save and Update</Button>
                    </EditButtonContainer>
                )}
            </Grid>
        </>
    )
}

export default Notes
